import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from "@angular/core";
import { Router } from "@angular/router";
import { Message } from "@bbwt/classes";
import { AppStorage } from "@bbwt/utils/app-storage";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { MedicalRecord, PatientLimitedNhsAccessInfo } from "@project/models/im1/patient-limited-nhs-access-info";
import { IM1DataService } from "@project/services/im1-data.service";
import { IM1ScreenEndpoint, IM1ScreenEndpointItem } from "@project/services/im1-screen-edpoint";
import { IM1Service } from "@project/services/im1.service";
import * as moment from "moment";
import { MessageService } from "primeng/api";

@Component({
    selector: "m-im1-screen-title",
    templateUrl: "./im1-screen-title.component.html",
    styleUrls: ["./im1-screen-title.component.scss"]
})
export class IM1ScreenTitleComponent implements OnInit, OnChanges {
    @Input() endpoint: IM1ScreenEndpoint;
    @Input() isNHSRestriction: boolean;
    @Input() sarNotificationCanBeViewed: boolean = true;
    @Input() responseTime: Date | null;
    @Output() readonly secondaryEmptyMessage: EventEmitter<boolean> = new EventEmitter();
    @Output() readonly limitedAccessResponded: EventEmitter<boolean> = new EventEmitter();
    @Output() readonly restrictedGPAccess: EventEmitter<boolean> = new EventEmitter();
    @Output() readonly isEmisPatient: EventEmitter<boolean> = new EventEmitter();

    protected lastUpdated: { lastUpdateDate?: string; im1System?: string };
    protected initializing = true;

    private im1DataService = inject(IM1DataService);
    private patientService = inject(PatientService);
    private messageService = inject(MessageService);
    private im1Service = inject(IM1Service);
    private userHelperService = inject(UserHelperService);
    private router = inject(Router);

    limitedAccessInfo: PatientLimitedNhsAccessInfo = null;

    async ngOnInit() {
        await this.initializeComponent();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["responseTime"] && !changes["responseTime"].isFirstChange()) {
            this.updateLastUpdated();
        }
    }

    private async initializeComponent() {
        const storedData = JSON.parse(AppStorage.getItem(AppStorage.IsRegisteredErrorMessage));
        const noNhsConnection = storedData?.errorMessage?.includes(
            "We're sorry, we could not connect to the NHS Service. Please try again later"
        );

        if (noNhsConnection && (storedData.patientId === this.patientService.currentPatientId || this.userHelperService.isPatient)) {
            this.secondaryEmptyMessage.emit(true);
            this.messageService.add(Message.Warning(storedData.errorMessage));
            return;
        } else if (!noNhsConnection) {
            AppStorage.setItem(
                AppStorage.IsRegisteredErrorMessage,
                JSON.stringify({
                    errorMessage: null,
                    patientId: null
                })
            );
        }

        this.limitedAccessInfo = await this.im1Service.patientHasLimitedAccess();

        if (this.userHelperService.isPatient && !this.limitedAccessInfo?.isRegistered && this.isIm1Screen) {
            this.router.navigate(["/app"]);
        }

        const emisGPRestrictedError =
            "We're sorry, we could not link your account to GP data. Please log in with your NHS login again to refresh your data, or contact your GP for further assistance.";

        if (noNhsConnection) {
            this.secondaryEmptyMessage.emit(true);
            return;
        } else if (
            this.userHelperService.isPatient &&
            (this.limitedAccessInfo?.errorMessage?.includes(emisGPRestrictedError) ||
                storedData?.errorMessage?.includes(emisGPRestrictedError)) &&
            this.isIm1Screen
        ) {
            this.router.navigate(["/app"]).then(() => {
                this.messageService.add(
                    Message.Warning(
                        "There was an issue connecting to your GP record. Please try again. If the problem persists, please refer to the Relevant Frequently Asked Questions (FAQ) section"
                    )
                );
            });
        }

        if (!this.limitedAccessInfo?.isRegistered || this.limitedAccessInfo?.hasLimitedAccess) {
            this.restrictedGPAccess.emit(true);
        }

        const key = this.endpointMapping[this.endpointItem];
        const hasMedicalRecordAccess = key ? this.limitedAccessInfo?.medicalRecord?.[key] === true : false;

        if (hasMedicalRecordAccess) {
            this.restrictedGPAccess.emit(false);
        }

        this.limitedAccessResponded.emit(true);

        if (
            this.limitedAccessInfo.isPatientLogged &&
            this.limitedAccessInfo?.errorMessage?.includes("Your link with NHS data has expired")
        ) {
            this.messageService.add(
                Message.Warning(
                    "Currently, access to your NHS data is limited. Please contact your GP and organization for further information"
                )
            );
        }

        if (this.limitedAccessInfo.isPatientLogged || this.limitedAccessInfo.IsSelectedPatient) {
            this.im1DataService.updateInBackground(this.patientService.currentPatientId);
        }

        await this.updateLastUpdated();

        this.initializing = false;
    }

    public async updateLastUpdated() {
        const lastUpdateInfo = await this.im1DataService.getLastUpdateInfo(this.patientService.currentPatientId, this.endpointItem);
        const im1System = lastUpdateInfo?.im1System?.toUpperCase();

        let lastUpdateDate = (lastUpdateInfo?.lastUpdate && moment(lastUpdateInfo.lastUpdate).format("DD/MM/YYYY HH:mm")) ?? "—";

        if (this.responseTime && this.responseTime.toISOString().split("T")[0] !== "1990-01-01") {
            lastUpdateDate = moment(this.responseTime).format("DD/MM/YYYY HH:mm");
        }

        if (this.isNHSRestriction) {
            lastUpdateDate = moment().format("DD/MM/YYYY HH:mm");
        }

        this.lastUpdated = { lastUpdateDate, im1System };
    }

    protected async accessRequire() {
        if (!this.limitedAccessInfo?.hasLimitedAccess) {
            this.messageService.add(Message.Success("Full medical record access granted"));
        } else {
            await this.im1Service.requestMedicalRecordAccess();
            this.messageService.add(
                Message.Warning("We have requested Full medical record access to NHS data on your behalf", "Request Medical Record Access")
            );
        }
    }

    async changedPatient() {
        if (this.limitedAccessInfo) {
            await this.initializeComponent();
        }
    }

    private endpointMapping: { [key in IM1ScreenEndpointItem]?: keyof MedicalRecord } = {
        allergies: "allergiesEnabled",
        consultations: "consultationsEnabled",
        documents: "documentsEnabled",
        "prescription-groups": "medicationEnabled",
        immunizations: "immunisationsEnabled",
        problems: "problemsEnabled",
        "emis-investigations": "testResultsEnabled",
        "tpp-investigations": "testResultsEnabled"
    };

    private get endpointItem() {
        return typeof this.endpoint === "string" ? this.endpoint : this.endpoint.endpoint;
    }

    private get isIm1Screen() {
        return typeof this.endpoint === "object" ? this.endpoint.isIm1Screen : true;
    }
}
